import { Button } from '@cloudflare/kumo';
import { ArrowUp, Stop } from '@phosphor-icons/react';
import type { AttachmentQueue } from '../use-chat-attachments.ts';
import { ChatAttachments } from './chat-attachments.tsx';

export function ChatComposer({
  value,
  onChange,
  onSubmit,
  canSend,
  attachments,
  label = 'Message',
  placeholder,
  autoFocus = false,
  sending = false,
  stop,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  canSend: boolean;
  attachments: AttachmentQueue;
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;
  sending?: boolean;
  stop?: { onClick: () => void; pending: boolean };
}) {
  return (
    <form
      aria-label="Message composer"
      className="shrink-0 rounded-[18px] border border-kumo-line bg-kumo-base p-3 shadow-[0_2px_8px_var(--color-kumo-shadow-drop)]"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSend) onSubmit();
      }}
    >
      <textarea
        aria-label={label}
        className="block field-sizing-content min-h-12 max-h-45 w-full resize-none border-0 bg-transparent px-2.5 py-2 text-[15px] leading-[1.6] text-kumo-default outline-none placeholder:text-kumo-subtle"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
          event.preventDefault();
          if (canSend) event.currentTarget.form?.requestSubmit();
        }}
        placeholder={placeholder}
        rows={1}
        autoFocus={autoFocus}
      />
      <div className="mt-1.5 flex items-center justify-between gap-3.5 [&_.chat-attachments]:p-0">
        <ChatAttachments queue={attachments} disabled={sending} />
        <Button
          type={stop ? 'button' : 'submit'}
          variant="primary"
          shape="circle"
          aria-label={stop ? 'Stop' : 'Send'}
          title={stop ? 'Stop' : 'Send'}
          onClick={stop?.onClick}
          disabled={stop ? stop.pending : !canSend}
        >
          {stop ? <Stop size={18} weight="fill" /> : <ArrowUp size={20} weight="bold" />}
        </Button>
      </div>
    </form>
  );
}
